import { clearClientAuthState } from "@/lib/api/auth-token";
import { getUserErrorMessage } from "@/lib/api/errors";
import { clearSignInPending } from "@/lib/auth/sign-in-pending";

const LOGIN_ERROR_PARAM = "error";
const MAX_ERROR_LENGTH = 280;

/** Default login landing path (auth route group). */
export function getLoginPath(): string {
  return "/";
}

/** Read SSO / auth failure message from the current URL (`?error=`). */
export function readLoginErrorFromLocation(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const raw =
    params.get(LOGIN_ERROR_PARAM)?.trim() ||
    params.get("error_description")?.trim() ||
    "";
  return raw || null;
}

/** Strip error query params so refresh does not re-show the toast. */
export function clearLoginErrorFromLocation(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (
    !url.searchParams.has(LOGIN_ERROR_PARAM) &&
    !url.searchParams.has("error_description")
  ) {
    return;
  }
  url.searchParams.delete(LOGIN_ERROR_PARAM);
  url.searchParams.delete("error_description");
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, document.title, next);
}

/** Map raw IdP / API messages to a safe user-facing string. */
export function formatLoginErrorMessage(raw: unknown): string {
  return getUserErrorMessage(raw, "sign-in");
}

/**
 * Clear local session and send the user to the login page with `?error=`.
 * Does not call Azure logout (failed sign-in should not start IdP logout).
 */
export function redirectToLoginWithError(rawMessage: unknown): void {
  if (typeof window === "undefined") return;

  clearSignInPending();
  clearClientAuthState();

  const message = formatLoginErrorMessage(rawMessage).slice(0, MAX_ERROR_LENGTH);
  const url = new URL(getLoginPath(), window.location.origin);
  url.searchParams.set(LOGIN_ERROR_PARAM, message);
  window.location.replace(url.toString());
}
